export {
  DISPATCH_MODE,
  DISPATCH_MODE_OPTIONS,
  inferDispatchMode,
  isSelfDispatchMode,
  sortTransportersByName,
  wasDispatchedAsSelf
} from './dispatchMode';

/**
 * WMS-GRN logistics scope (TASK-4132): vehicle/transporter capture + E-Way Bill cross-validation
 * apply only to Internal Stock Transfer SOs whose GRN is created via WMS — i.e. the buyer shipping
 * address is WMS-integrated (`is_manual_grn === false`). Manual-GRN IST and every other invoice type
 * (e.g. Domestic Online FBA/FBF/Myntra SJIT) never produce a WMS GRN and are out of scope.
 */
export const isStockTransferLogisticsScope = (invoiceType, isManualGrn) =>
  invoiceType === 'INTERNAL_STOCK_TRANSFER' && isManualGrn === false;

/** Dispatch: mandatory vehicle + transporter when Mensa (or IST where responsibility is always Mensa). */
export const isDispatchLogisticsMandatory = (deliveryResponsibility, invoiceType) => {
  if (invoiceType === 'INTERNAL_STOCK_TRANSFER') {
    return true;
  }
  return deliveryResponsibility === 'Mensa';
};

const normalizeLogisticsValue = (value) => (value || '').trim().toLowerCase();

/**
 * Resolve the MDM registered transporter name (same as BE
 * `MdmUtils.getTransporterDetailsByTransporterNames` → registered_transport_name).
 * Accepts registered name or transporter id.
 */
export const resolveMdmRegisteredTransporterName = (transporterName, transporters = []) => {
  if (!transporterName) {
    return null;
  }
  const normalized = normalizeLogisticsValue(transporterName);
  const match = transporters.find((transporter) => {
    const registered = normalizeLogisticsValue(transporter?.registered_transport_name);
    const id = transporter?.id != null ? String(transporter.id) : '';
    return registered === normalized || (id && id === String(transporterName));
  });
  return match?.registered_transport_name || null;
};

/**
 * Cross-validate transporter against active E-Way Bill when present.
 * Vehicle is never matched to the EWB on dispatch/delivery (UAT TASK-4132).
 * Transporter is compared using the MDM registered name when `transporters` is provided
 * (aligned with BE). Skips transporter checks for Self dispatch mode.
 * Skips when no E-Way Bill exists (defensive only for SO —
 * primary gate is transitions/bulk). RO RTO/Partial RTO/RTB require EWB in roLogistics.js.
 */
export const validateLogisticsAgainstEwayBill = ({
  transporterName,
  ewayBillDetails,
  skipTransporterValidation = false,
  transporters
}) => {
  if (!ewayBillDetails?.document_number) {
    return null;
  }

  if (skipTransporterValidation || !transporterName) {
    return null;
  }

  const ewbTransporter = ewayBillDetails?.transporter_name;
  const resolvedName = Array.isArray(transporters)
    ? resolveMdmRegisteredTransporterName(transporterName, transporters)
    : transporterName;
  if (Array.isArray(transporters) && !resolvedName) {
    return `Transporter "${transporterName}" is not registered in MDM.`;
  }
  const nameToCompare = resolvedName || transporterName;
  if (ewbTransporter && normalizeLogisticsValue(nameToCompare) !== normalizeLogisticsValue(ewbTransporter)) {
    return `Transporter name does not match E-Way Bill (${ewbTransporter}).`;
  }

  return null;
};
